import Box from '@mui/material/Box'
import { DELIM } from 'config'
import { createRef, useState, useEffect, useRef } from 'react'

import { CategoryOpt } from './common'
import ItemsMultiSelector from './ItemsMultiSelector'
import MidController from './MidController'
import OrderController from './OrderController'
import SelectorBox from './SelectorBox'

const HIDE_EMPTY_CATE = true

interface Props {
  leftTitle: string
  rightTitle: string
  optsState: CategoryOpt[] | null
  setOptsState: (optsState: CategoryOpt[]) => void
  showCateController?: boolean
}

function getSelectedCates(selected: string[]) {
  return new Set(selected.map((i) => i.split(DELIM)[0]))
}

function getTopBottomOpts(options: HTMLOptionsCollection) {
  const rtn = [null, null] as (HTMLOptionElement | null)[]
  for (let i = 0; i < options.length; i++) {
    if (options[i].selected) {
      rtn[0] = rtn[0] || options[i]
      rtn[1] = options[i]
    }
  }
  return rtn
}

const TwoSideMuntiSelector = (props: Props) => {
  const { optsState, leftTitle, rightTitle, setOptsState, showCateController = false } = props

  const leftRef = createRef<HTMLSelectElement>()
  const rightRef = createRef<HTMLSelectElement>()
  const [selected, setSelected] = useState<string[]>([])
  const [isRightSelected, setIsRightSelected] = useState<boolean>() // left: false, right: true, undefined
  const movedUpDown = useRef<'up' | 'down'>()

  useEffect(() => {
    const [topOpt, btmOpt] = getTopBottomOpts(rightRef.current!.options)
    if (movedUpDown.current) {
      if (topOpt && btmOpt) {
        const cRect = rightRef.current!.getBoundingClientRect()
        const tOpt = movedUpDown.current === 'up' ? topOpt : btmOpt
        const tRect = tOpt.getBoundingClientRect()
        if (tRect.top < cRect.top) {
          tOpt.scrollIntoView() // fixme: Safari
        }
        if (tRect.bottom > cRect.bottom) {
          tOpt.scrollIntoView(false) // fixme: Safari
        }
      }
      movedUpDown.current = undefined
    }
  })

  return (
    <Box display="flex" justifyContent="center" minHeight={485}>
      <SelectorBox title={leftTitle}>
        <ItemsMultiSelector
          opts={optsState || undefined}
          onChange={(e) => {
            const { selectedOptions } = e.target as any
            const localSelected = [] as string[]
            for (let i = 0; i < selectedOptions.length; i++) {
              localSelected.push(selectedOptions[i].value as string)
            }
            setSelected(localSelected)
            setIsRightSelected(false)
          }}
          value={selected}
          isPicked={false}
          hideEmptyCate={HIDE_EMPTY_CATE}
          ref={leftRef}
          testid="items-multi-selector-left"
        />
      </SelectorBox>
      <MidController
        onAllLeftClick={() => {
          setOptsState(
            optsState!.map((c) => ({
              ...c,
              items: c.items.map((i) => ({ ...i, picked: false })),
            })),
          )
          setIsRightSelected(false)
        }}
        onAllRightClick={() => {
          setOptsState(
            optsState!.map((c) => ({
              ...c,
              items: c.items.map((i) => ({ ...i, picked: true })),
            })),
          )
          setIsRightSelected(true)
        }}
        onLeftClick={() => {
          setOptsState(
            optsState!.map((c) => ({
              ...c,
              items: c.items.map((i) => ({ ...i, picked: i.picked && !selected.includes(i.key) })),
            })),
          )
          setIsRightSelected(false)
        }}
        onRightClick={() => {
          setOptsState(
            optsState!.map((c) => ({
              ...c,
              items: c.items.map((i) => ({ ...i, picked: i.picked || selected.includes(i.key) })),
            })),
          )
          setIsRightSelected(true)
        }}
      />
      <SelectorBox title={rightTitle}>
        <ItemsMultiSelector
          opts={optsState || undefined}
          onChange={(e) => {
            const { selectedOptions } = e.target as any
            const localSelected = [] as string[]
            for (let i = 0; i < selectedOptions.length; i++) {
              localSelected.push(selectedOptions[i].value as string)
            }
            setSelected(localSelected)
            setIsRightSelected(true)
          }}
          value={selected}
          ref={rightRef}
          isPicked
          hideEmptyCate={HIDE_EMPTY_CATE}
          testid="items-multi-selector-right"
        />
      </SelectorBox>
      <OrderController
        disabled={!isRightSelected}
        ducumentTypeHidden={!showCateController}
        documentTypeMoreThen2={optsState ? optsState.length > 1 : false}
        onOneUpClick={() => {
          if (!isRightSelected) return
          setOptsState(
            optsState!.map(({ name, items, ...rest }) => {
              for (let i = 1; i < items.length; i++) {
                // starts from 1
                if (!selected.includes(items[i].key)) continue
                let target = -1
                for (let j = i - 1; j >= 0; j--) {
                  if (!selected.includes(items[j].key) && items[j].picked === isRightSelected) {
                    target = j
                    break
                  }
                }
                if (target > -1) {
                  items.splice(target, 0, items[i])
                  items.splice(i + 1, 1)
                }
              }
              return { name, items, ...rest }
            }),
          )
          movedUpDown.current = 'up'
        }}
        onOneDownClick={() => {
          if (!isRightSelected) return
          setOptsState(
            optsState!.map(({ name, items, ...rest }) => {
              for (let i = items.length - 2; i > -1; i--) {
                // start from bottom
                if (!selected.includes(items[i].key)) continue
                let target = -1
                for (let j = i + 1; j < items.length; j++) {
                  if (!selected.includes(items[j].key) && items[j].picked === isRightSelected) {
                    target = j + 1
                    break
                  }
                }
                if (target > -1) {
                  items.splice(target, 0, items[i])
                  items.splice(i, 1)
                }
              }
              return { name, items, ...rest }
            }),
          )
          movedUpDown.current = 'down'
        }}
        onTopClick={() => {
          if (!isRightSelected) return
          setOptsState(
            optsState!.map(({ name, items, ...rest }) => {
              return {
                name,
                items: [
                  ...items.filter((i) => selected.includes(i.key)),
                  ...items.filter((i) => !selected.includes(i.key)),
                ],
                ...rest,
              }
            }),
          )
          movedUpDown.current = 'up'
        }}
        onBottomClick={() => {
          if (!isRightSelected) return
          setOptsState(
            optsState!.map(({ name, items, ...rest }) => {
              return {
                name,
                items: [
                  ...items.filter((i) => !selected.includes(i.key)),
                  ...items.filter((i) => selected.includes(i.key)),
                ],
                ...rest,
              }
            }),
          )
          movedUpDown.current = 'down'
        }}
        onDocumentTypeOneUpClick={() => {
          const selectedCats = getSelectedCates(selected)
          const r = [...optsState!]
          for (let i = 1; i < r.length; i++) {
            // starts from 1
            if (selectedCats.has(r[i].key) && !selectedCats.has(r[i - 1].key)) {
              r.splice(i - 1, 0, r[i])
              r.splice(i + 1, 1)
            }
          }
          setOptsState(r)
          movedUpDown.current = 'up'
        }}
        onDocumentTypeOneDownClick={() => {
          const selectedCats = getSelectedCates(selected)
          const r = [...optsState!]
          for (let i = r.length - 2; i > -1; i--) {
            // start from bottom
            if (selectedCats.has(r[i].key) && !selectedCats.has(r[i + 1].key)) {
              r.splice(i + 2, 0, r[i])
              r.splice(i, 1)
            }
          }
          setOptsState(r)
          movedUpDown.current = 'down'
        }}
      />
    </Box>
  )
}

export default TwoSideMuntiSelector
